import { useState, useRef, useEffect, useMemo } from "react";

const useMultiSelect = ({ value = [], onChange, options = [] }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const selectAllRef = useRef(null);

    // Memoized values (performance safe)
    const allValues = useMemo(
        () => options.map((opt) => opt.value),
        [options]
    );

    const allSelected = value.length > 0 && value.length === allValues.length;
    const anySelected = value.length > 0 && value.length < allValues.length;

    const toggleOption = (optionValue) => {
        if (value.includes(optionValue)) {
            onChange(value.filter((v) => v !== optionValue));
        } else {
            onChange([...value, optionValue]);
        }
    };

    const toggleSelectAll = () => {
        onChange(allSelected ? [] : allValues);
    };

    // Indeterminate state
    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate = anySelected;
        }
    }, [anySelected]);

    // Outside click handler
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                setOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const toggleDropdown = () => setOpen((prev) => !prev);

    const handleRemoveTag = ({ value, event }) => {
        event.stopPropagation();
        toggleOption(value);
    };

    const handleClearAll = (event) => {
        event.stopPropagation();
        onChange([]);
    };

    return {
        open,
        setOpen,
        ref,
        selectAllRef,
        allSelected,
        toggleOption,
        toggleSelectAll,
        toggleDropdown,
        handleRemoveTag,
        handleClearAll
    };
};

export default useMultiSelect;
